import { FileText, Maximize2, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import type { EllieArtifact } from '../../services/supportRequest.service';

/**
 * An artifact referenced by a message.
 *
 * Title, description and a way in — deliberately no body preview. An artifact is
 * usually a long document, and inlining even part of one buries the conversation
 * it was posted into. The document is read in the takeover, where there's room
 * for it and the thread stays alongside.
 */
export function ArtifactCard({
  artifact,
  onOpen,
  /**
   * False when this card sits in the takeover that is already showing this
   * artifact — "View full" would just reopen what the reader is looking at.
   * Other artifacts in the same thread keep theirs, since opening one of those
   * does something.
   */
  showViewFull = true,
  /** Detach this artifact from the message. Omitted for anyone who can't. */
  onRemove,
}: {
  artifact: EllieArtifact;
  onOpen: () => void;
  showViewFull?: boolean;
  onRemove?: () => void;
}) {
  return (
    <div
      className={`relative mt-2 w-full text-left rounded-lg border border-border bg-card ${
        showViewFull ? 'hover:bg-accent/40 transition-colors' : ''
      }`}
    >
      {/* The whole card opens the artifact, but "Delete" can't be nested inside
          that button — a button inside a button is invalid, and the click would
          fall through to opening the thing you just removed. So the card-wide
          target is a sibling overlay behind the content, and the actions sit
          above it with their pointer events restored. */}
      {showViewFull && (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`View ${artifact.name}`}
          className="absolute inset-0 rounded-lg"
        />
      )}

      <div className="relative pointer-events-none flex items-start justify-between gap-3 px-4 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <FileText className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {artifact.name}
            </p>
            {artifact.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {artifact.description}
              </p>
            )}
          </div>
        </div>

        {(showViewFull || onRemove) && (
          <div className="pointer-events-auto flex flex-col items-stretch gap-0.5 flex-shrink-0">
            {showViewFull && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 justify-start"
                onClick={onOpen}
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1" />
                View full
              </Button>
            )}
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 justify-start text-muted-foreground hover:text-destructive"
                onClick={onRemove}
                title="Remove this artifact from the message"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
